import React, { useCallback, useState, useEffect } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import moment from "moment";
import {
  LocationsSearcher,
  Store__AtsStats,
  Store__System,
  PosthogStore
} from "@talentbait/shared-components";

import {
  ChatFormWrapper,
  ContentContainer,
  ContentTitle,
  ContentTitleFooter,
  customStyles,
  customStylesCompanies,
  NextButton,
  RemoteJobCheckbox,
  TextInput
} from "./ChatForm.styled";
import { Group } from "../../../../Components/Layout";
import Actions from "../../../../Actions";
import { withRefluxStore } from "../../../../Stores";
import AdAssistantStore from "../../AdAssistantStore";
import { fetchRecommendationStream, mapAssets } from "../../utils";
import logger from "../../../../utils/LoggerFrontend";
import { ActionsAdAssistant } from "../../../../Actions/ActionsAdAssistant/ActionsAdAssistant";
import { notifyAssistantCampaignStartedSlack } from "../../slack-alerts/notifyAssistantCampaignStartedSlack";
import { withRouter } from "react-router-dom";
import { withHttp } from "../../../SelfServiceCampaignCreation/Previews/utils";
import { Heading } from "../../../../Components/Heading";
import AssistantBudgetCalculator from "../../budget-check/assistant-budget-calculator/AssistantBudgetCalculator";
import { getMinimumClicks } from "../../../../utils/Helpers";
import BudgetCheckNavigation from "./budget-check-navigation/BudgetCheckNavigation";
import { useTranslation } from "react-i18next";

const CAMPAIGN_DURATION_OPTIONS = /** @type {const} */ ([
  { label: "{{duration}} days", value: 30, displayDuration: 30 },
  { label: "{{duration}} days", value: 60, displayDuration: 60 },
  { label: "{{duration}} days", value: 90, displayDuration: 90 },
  { label: "{{duration}} months", value: 180, displayDuration: 6 },
  { label: "{{duration}} months", value: 365, displayDuration: 12 },
  { label: "{{duration}} months", value: 730, displayDuration: 24 },
  { label: "{{duration}} days", value: 19, displayDuration: 19 },
  { label: "{{duration}} weeks", value: 42, displayDuration: 6 },
  { label: "Until the end of the year", value: 365 - moment().dayOfYear() }
]);

const ChatForm = ({
  emitLoading,
  isBudgetCheck,
  isOnboardingCampaign,
  // Router
  history,
  match,
  // Store
  organization,
  userProfile,
  atsStats
}) => {
  const { t } = useTranslation();
  const { campaignId } = match.params;

  const CAMPAIGN_LOCALE_OPTIONS = /** @type {const} */ ([
    { label: t("English"), value: "en_US" },
    { label: t("German"), value: "de_DE" },
    { label: t("Spanish"), value: "es_ES" }
  ]);

  const [companyName, setCompanyName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [jobPostLink, setJobPostLink] = useState("");
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);

  const [calculateBudget, setCalculateBudget] = useState(false);
  const [minClicksPackage, setMinClicksPackage] = useState(null);
  const [campaignBudgetData, setCampaignBudgetData] = useState({});

  // Extra
  const [isRemoteJob, setIsRemoteJob] = useState(false);
  const [startDate, setStartDate] = useState("now");
  const [duration, setDuration] = useState(30);
  const languageMap = {
    de: "de_DE",
    en: "en_US",
    es: "es_ES"
  };

  const [language, setLanguage] = useState(languageMap[userProfile?.language] || "de_DE");
  const [activeDate, setActiveDate] = useState("now");

  const [errors, setErrors] = useState({
    companyName: false,
    jobTitle: false,
    jobPostLink: false,
    locations: false,
    duration: false,
    language: false
  });

  useEffect(() => {
    if (organization?.id) {
      ActionsAdAssistant.getAllCompanies(organization.id);
      const getAllCompaniesCompletedRef = ActionsAdAssistant.getAllCompanies.completed.listen(
        (result) => {
          const { payload } = result;
          const companyOptions = payload.map((company) => ({
            label: company.name,
            value: company.name
          }));
          setCompanies(companyOptions);
          setCompanyName(companyOptions[0].value);
          getAllCompaniesCompletedRef();
        }
      );
    }
  }, [organization?.id]);

  useEffect(() => {
    if (isOnboardingCampaign && campaignId) {
      ActionsAdAssistant.getAssistantCampaign(campaignId);
      const getAssistantCampaignCompletedRef =
        ActionsAdAssistant.getAssistantCampaign.completed.listen((result) => {
          const { payload } = result;

          setJobTitle(payload.jobTitle);
          setJobPostLink(payload.jobPostingUrl);
          setLocations(payload.locations);
          setActiveDate("now");
          setStartDate(payload.startDate);
          setDuration(payload.campaignDuration);

          getAssistantCampaignCompletedRef();
        });
    }
  }, [isOnboardingCampaign, campaignId]);

  useEffect(() => {
    if (activeDate === "now") {
      setStartDate("");
    }
  }, [activeDate]);

  const handleDateChange = (e) => {
    setActiveDate(e.target.value);
    if (e.target.value === "later") {
      setStartDate(startDate || "");
    }
  };

  const handleInputChange = (setter, field) => (e) => {
    const value = e.target.value;
    setter(value);
    if (errors[field]) {
      setErrors((prevErrors) => ({ ...prevErrors, [field]: value.trim() === "" }));
    }
  };

  const handleInputCheckChange = (setter) => (e) => {
    setter(e.target.checked);
  };

  const handleLocationSelect = (location) => {
    setLocations(location);
    if (errors.locations) {
      setErrors((prevErrors) => ({ ...prevErrors, locations: location.length === 0 }));
    }
  };

  const handleSelectChange = (setter, field) => (selectedOption) => {
    if (selectedOption) {
      setter(selectedOption.value);
      if (errors[field]) {
        setErrors((prevErrors) => ({ ...prevErrors, [field]: selectedOption.value === "" }));
      }
    }
  };

  const handleCompanySelectChange = (selectedOption) => {
    if (selectedOption) {
      setCompanyName(selectedOption.value);
      setErrors((prevErrors) => ({ ...prevErrors, companyName: false }));
    } else {
      setCompanyName("");
      setErrors((prevErrors) => ({ ...prevErrors, companyName: true }));
    }
  };

  const searchShutterStock = useCallback(
    (keywords, campaign, jobCategoryId) => {
      if (keywords) {
        const joinedSearch = keywords;
        Actions.searchShutterstockAssets(joinedSearch);
      }

      const ref = Actions.searchShutterstockAssets.completed.listen((result) => {
        Actions.setTaskStep(3);
        const { payload } = result;

        const {
          images: { data: images, searchId: imagesSearchId },
          videos: { data: videos, searchId: videosSearchId }
        } = payload;

        const {
          jobTitle,
          jobPostLink,
          location,
          companyName,
          companyId,
          activeDate,
          duration,
          startDate,
          language,
          isRemoteJob
        } = campaign;
        const campaignData = { jobPostLink: jobPostLink };

        fetchRecommendationStream({
          formality: atsStats?.formality,
          organization,
          userProfile,
          campaignData,
          newJobTitle: jobTitle,
          Actions: ActionsAdAssistant
        }).then(async (streamedText) => {
          const { teaserText } = streamedText;
          const defaultText = { long: teaserText, short: teaserText };

          const mappedAssets = mapAssets({
            shutterStockImages: images,
            shutterStockVideos: videos,
            uploadedAssets: [],
            imagesSearchId,
            videosSearchId
          });

          const campaignGoalOnOrgaLevel = organization?.campaignGoal;
          const campaignGoal = campaignGoalOnOrgaLevel || "traffic";

          const data = {
            ...campaignBudgetData,
            companyName,
            messages: [],
            jobCategoryId,
            jobTitle,
            jobPostLink,
            keywords,
            locations: location,
            organizationId: organization.id,
            organizationName: organization.name,
            orgaName: organization.name,
            mediaArray: mappedAssets,
            defaultText,
            // EXTRA PROPS FOR BACKWARD COMPATIBILITY
            companyId,
            deployed: true,
            campaignName: jobTitle,
            jobPostingUrl: jobPostLink,
            selectedChannels: campaignBudgetData.selectedChannels || [],
            channels: [],
            campaignGoal,
            userName: userProfile.userName,
            startDate,
            campaignDuration: parseInt(duration),
            locale: language,
            isRemoteJob,
            activeDate
          };

          if (isOnboardingCampaign && campaignId) {
            ActionsAdAssistant.updateAssistantCampaign(data, campaignId);

            const updateCampaignSuccessfully =
              ActionsAdAssistant.updateAssistantCampaign.completed.listen((result) => {
                Actions.setTaskStep(4);
                const { payload } = result;
                const campaignId = payload.id;
                notifyAssistantCampaignStartedSlack({
                  Actions,
                  userName: userProfile.userName,
                  userId: userProfile.id,
                  email: userProfile.email,
                  orgaName: organization.name,
                  orgaId: organization.id,
                  campaignId
                });

                emitLoading(false);
                history.push(`/assistant/preview/${campaignId}`);
                updateCampaignSuccessfully();
              });
          } else {
            ActionsAdAssistant.createAssistantCampaign(data);
            const createCampaignSuccessfully =
              ActionsAdAssistant.createAssistantCampaign.completed.listen((result) => {
                Actions.setTaskStep(4);
                const { payload } = result;
                const campaignId = payload.id;
                notifyAssistantCampaignStartedSlack({
                  Actions,
                  userName: userProfile.userName,
                  userId: userProfile.id,
                  email: userProfile.email,
                  orgaName: organization.name,
                  orgaId: organization.id,
                  campaignId
                });

                emitLoading(false);
                history.push(`/assistant/preview/${campaignId}`);
                createCampaignSuccessfully();
              });

            ref();
          }
        });
      });

      const refFailed = Actions.searchShutterstockAssets.failed.listen((error) => {
        logger.error(
          "ChatComplete:searchShutterStock",
          "search shutterstock images api failed: ",
          error
        );
        emitLoading(false);
        refFailed();
      });
    },
    [emitLoading, organization, atsStats, userProfile, history, campaignBudgetData]
  );

  const getJobCategory = useCallback(
    async (campaign) => {
      const { jobTitle, jobPostLink, keywords } = campaign;

      try {
        emitLoading(true);

        Actions.getJobCategory({ jobPostingUrl: jobPostLink, jobTitle });
        const unsubscribeCompletedJobCategory = Actions.getJobCategory.completed.listen(
          async (result) => {
            try {
              Actions.setTaskStep(2);
              const jobCategoryId = result.payload.id;
              searchShutterStock(keywords, campaign, jobCategoryId);
            } finally {
              unsubscribeCompletedJobCategory();
            }
          }
        );
      } catch (error) {
        emitLoading(false);
      }
    },
    [searchShutterStock, emitLoading]
  );

  const handleCreateCampaign = () => {
    emitLoading(true);

    Actions.setTaskStep(0);
    Actions.getAssistantKeywordsByJobTitle(jobTitle);

    const getAssistantKeywordsByJobTitleCompletedRef =
      Actions.getAssistantKeywordsByJobTitle.completed.listen((result) => {
        const { payload } = result;
        const keywords = payload;

        Actions.getOrCreateCompany({ name: companyName, organizationId: organization.id });
        const getOrCreateCompanyCompletedRef = Actions.getOrCreateCompany.completed.listen(
          (result) => {
            const { payload } = result;
            const companyId = payload.id;
            Actions.setTaskStep(1);
            getJobCategory({
              companyName,
              jobTitle,
              jobPostLink,
              location: locations,
              companyId,
              keywords,
              startDate,
              duration,
              language,
              isRemoteJob,
              activeDate
            });

            Actions.setCompanyId(companyId);
            getOrCreateCompanyCompletedRef();
          }
        );
        getAssistantKeywordsByJobTitleCompletedRef();
      });
  };

  const handleJobPostLinkChange = (event) => {
    const url = event.target.value;
    setJobPostLink(withHttp(url));
    if (errors.jobPostLink) {
      setErrors((prevErrors) => ({ ...prevErrors, jobPostLink: url.trim() === "" }));
    }
  };

  let durationOptionsToShow = [...CAMPAIGN_DURATION_OPTIONS];

  if (organization?.signupType == "PLG") {
    durationOptionsToShow.unshift({ label: "{{duration}} days", value: 14, displayDuration: 14 });
  }

  durationOptionsToShow = durationOptionsToShow.map(({ label, value, displayDuration }) => ({
    // eslint-disable-next-line react-i18n/no-dynamic-translation-keys
    label: t(label, { duration: displayDuration }),
    value
  }));

  const validateForm = () => {
    const newErrors = {
      companyName: companyName.trim() === "",
      jobTitle: jobTitle.trim() === "",
      jobPostLink: jobPostLink.trim() === "",
      locations: locations.length === 0,
      duration: duration === "",
      language: language === ""
    };
    setErrors(newErrors);
    return !Object.values(newErrors).includes(true);
  };

  const handleCreateCampaignClick = ({ createCampaign = false }) => {
    if (!validateForm()) {
      return;
    }

    if (!createCampaign) {
      // Add a URL parameter
      history.push({
        pathname: history.location.pathname,
        search: `?budgetCheck=true`
      });

      ActionsAdAssistant.setManualCampaignData({
        jobPostingUrl: jobPostLink,
        jobTitle,
        locations,
        orgaId: organization.id,
        orgaName: organization.name,
        locale: language,
        startDate,
        duration,
        isRemoteJob,
        activeDate
      });
      Actions.loadOrganizationOfCampaign(organization.id);
      setCalculateBudget(true);
    } else {
      handleCreateCampaign();
    }
  };

  /**
   * Calculates the minimum clicks for the campaign
   * @param {number} budget Lifetime budget for the campaign
   * @param {object[]} selectedChannels Selected channels for the campaign
   * @returns {number} Minimum clicks for the campaign
   */
  const calcMinClicks = (budget = 400, selectedChannels = ["youtube"]) => {
    logger.debug("calcMinClicks", "called", {
      budget,
      selectedChannels,
      minClicksPackage
    });

    if (minClicksPackage) {
      return minClicksPackage;
    }

    return getMinimumClicks(budget, selectedChannels);
  };

  const onShowPreview = (channel) => {
    // TODO: Implement preview functionality if needed
    logger.debug("onShowPreview", "called", { channel });
  };

  const handleNewCampaignData = (data) => {
    //Only update if data has actually changed
    if (JSON.stringify(campaignBudgetData) !== JSON.stringify(data)) {
      setCampaignBudgetData(data);
    }
  };

  if (!organization) {
    return null;
  }

  return (
    <>
      {calculateBudget ? (
        <>
          <ContentContainer width="100%">
            <BudgetCheckNavigation
              onBackStep={() => {
                setCalculateBudget(false);
              }}
              onNextStep={() => {
                handleCreateCampaignClick({ createCampaign: true });
              }}
            />
          </ContentContainer>

          <ContentContainer width="60%">
            <AssistantBudgetCalculator
              setCalculateBudget={setCalculateBudget}
              onShowPreview={onShowPreview}
              calcMinClicks={calcMinClicks}
              isBudgetCheck={true}
              setCampaignBudgetData={handleNewCampaignData}
            />
          </ContentContainer>
        </>
      ) : (
        <ChatFormWrapper>
          <Group direction="column" gap="10px">
            <Heading
              title={isBudgetCheck ? t("Perform budget check") : t("Create a new campaign")}
              subtitle={
                isBudgetCheck
                  ? t("Ask the system for a budget and channel recommendation now")
                  : t("Please fill in the necessary data to create the campaign.")
              }
            />
            <ContentContainer>
              <ContentTitle>{t("Company")}*</ContentTitle>
              <CreatableSelect
                id="company-select"
                isClearable
                options={companies}
                value={companies.find((company) => company.value === companyName)}
                onChange={handleCompanySelectChange}
                styles={customStylesCompanies}
                placeholder={t("Select a company...")}
                formatCreateLabel={(input) => t("Create company: {{input}}", { input })}
              />
              {errors.companyName && (
                <span style={{ color: "red" }}>{t("Company name is required.")}</span>
              )}
            </ContentContainer>
            <ContentContainer>
              <ContentTitle>{t("Job title")}*</ContentTitle>
              <TextInput
                type="text"
                name="jobTitle"
                value={jobTitle}
                onChange={handleInputChange(setJobTitle, "jobTitle")}
                placeholder={t("e.g. Marketing Manager")}
              />
              {errors.jobTitle && (
                <span style={{ color: "red" }}>{t("Job title is required.")}</span>
              )}
            </ContentContainer>
            <ContentContainer>
              <ContentTitle>{t("Job link")}*</ContentTitle>
              <TextInput
                type="text"
                name="jobPostLink"
                value={jobPostLink}
                onChange={handleJobPostLinkChange}
                placeholder={t("example_jobpost_url")}
              />
              {errors.jobPostLink && (
                <span style={{ color: "red" }}>{t("Job link is required.")}</span>
              )}
            </ContentContainer>
            <ContentContainer>
              <ContentTitle>{t("Location")}*</ContentTitle>
              <LocationsSearcher location={locations} onSelect={handleLocationSelect} />
              {errors.locations && (
                <span style={{ color: "red" }}>{t("At least one location is required.")}</span>
              )}
            </ContentContainer>
            <ContentContainer>
              <ContentTitle>
                <RemoteJobCheckbox
                  type="checkbox"
                  checked={isRemoteJob}
                  onChange={handleInputCheckChange(setIsRemoteJob)}
                />{" "}
                {t("Remote Job")}
              </ContentTitle>
            </ContentContainer>
            <Group direction="row" gap="40px" margin="10px 0 0 0">
              <ContentContainer minWidth="fit-content">
                <ContentTitleFooter>{t("Start Date")}</ContentTitleFooter>
                <Group direction="row" gap="10px" align="center">
                  <input
                    type="radio"
                    id="date-now"
                    value="now"
                    checked={activeDate === "now"}
                    onChange={handleDateChange}
                  />
                  <label htmlFor="date-now">{t("As of now")}</label>
                  <input
                    type="radio"
                    id="date-later"
                    value="later"
                    checked={activeDate === "later"}
                    onChange={handleDateChange}
                  />
                  <label htmlFor="date-later">{t("Later")}</label>
                  {activeDate === "later" && (
                    <input
                      type="date"
                      value={startDate}
                      onChange={handleInputChange(setStartDate, "startDate")}
                    />
                  )}
                </Group>
              </ContentContainer>
              <ContentContainer>
                <ContentTitleFooter>{t("Duration")}*</ContentTitleFooter>
                <div style={{ maxHeight: "40px", display: "inline-block", width: "100%" }}>
                  <Select
                    id="duration-select"
                    placeholder={t("Select duration")}
                    value={durationOptionsToShow.find((o) => o.value === duration)}
                    onChange={handleSelectChange(setDuration, "duration")}
                    options={durationOptionsToShow}
                    menuPlacement="auto"
                    styles={customStyles}
                  />
                </div>
                {errors.duration && (
                  <span style={{ color: "red" }}>{t("Duration is required.")}</span>
                )}
              </ContentContainer>
              <ContentContainer>
                <ContentTitleFooter>{t("Language")}* </ContentTitleFooter>
                <div style={{ maxHeight: "40px", display: "inline-block", width: "100%" }}>
                  <Select
                    id="language-select"
                    placeholder={t("Select language")}
                    value={CAMPAIGN_LOCALE_OPTIONS.find((o) => o.value === language)}
                    onChange={handleSelectChange(setLanguage, "language")}
                    options={CAMPAIGN_LOCALE_OPTIONS}
                    menuPlacement="auto"
                    styles={customStyles}
                  />
                </div>
                {errors.language && (
                  <span style={{ color: "red" }}>{t("Language is required.")}</span>
                )}
              </ContentContainer>
            </Group>
          </Group>
          <Group justify="flex-end" width="100%">
            <Group justify="flex-end" width="fit-content">
              <NextButton
                variant="primary"
                text={isBudgetCheck ? t("Go to budget selection") : t("Create campaign")}
                onClick={() => handleCreateCampaignClick({ createCampaign: !isBudgetCheck })}
              />
            </Group>
          </Group>
        </ChatFormWrapper>
      )}
    </>
  );
};

export default withRouter(
  withRefluxStore(
    ChatForm,
    [Store__System, Store__AtsStats, AdAssistantStore, PosthogStore],
    ["organization", "userProfile", "atsStats"]
  )
);
